/**
 * GatewayDeploymentProbeCard (OCP-882) - one row per gateway deployment with
 * the result of its last synthetic request.
 *
 * On 2026-10-16 the gateway's readiness (GatewayHealthCard) stayed green while
 * Scaleway stalled every embedding for 66 minutes. orboto Control now sends a
 * tiny real request to each deployment every 2 minutes; this card shows per
 * deployment whether it answered, how fast, the error, and since when it has
 * been in that state, its active and preferred order (OCP-949: OCP drives the
 * order from these probes, see GatewayFailoverCard) and a strip of recent probes.
 *
 * Self-fetching like the other gateway cards, so a failed read here never
 * blanks the model list. "Probe now" runs the same check immediately (audited)
 * to confirm a recovery without waiting for the next run.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

type ProbeStatus = 'ok' | 'slow' | 'error' | 'timeout';

interface ProbeSample {
  observed_at: string;
  status: ProbeStatus;
  latency_ms: number | null;
  error: string | null;
}

interface ProbeItem {
  model_id: string;
  model_name: string;
  kind: 'embedding' | 'chat';
  provider: string | null;
  api_base: string | null;
  order: number | null;
  status: ProbeStatus | null;
  ok: boolean | null;
  latency_ms: number | null;
  http_status: number | null;
  error: string | null;
  probed_at: string | null;
  last_ok_at: string | null;
  since: string | null;
  fail_streak: number;
  /** OCP-949: consecutive error / timeout runs. */
  error_streak?: number;
  /** OCP-949: the order of this deployment when its group is on its preferred order. */
  preferred_order?: number | null;
  /** OCP-949: the active primary of its group. */
  active?: boolean;
  group_state?: 'preferred' | 'failed_over' | 'held';
  /** p95 over the last `p95_window` probes (ms null = too few answered probes yet). */
  p95?: { ms: number | null; threshold_ms: number | null; breach: boolean };
  /** OCP-903: slow probes among the last `recent_window` answered ones (the current finding behind a p95 breach). */
  recent_slow?: { slow: number; samples: number };
  /** OCP-949: ok probes among the recent window (a failover candidate needs 3 of 4). */
  recent_ok?: { ok: number; probes: number };
  window: { hours: number; probes: number; ok: number; not_ok: number; p95_latency_ms: number | null; max_latency_ms: number | null };
  recent: ProbeSample[];
}

interface ProbeView {
  configured: boolean;
  enabled: boolean;
  thresholds: {
    timeout_ms: number;
    fail_streak: number;
    embedding_latency_ms: number;
    chat_latency_ms: number;
    cooldown_minutes: number;
    embedding_dimensions: number;
    interval_minutes: number;
    p95_window?: number;
    embedding_p95_ms?: number;
    chat_p95_ms?: number;
    removal_runs?: number;
    p95_clear_factor?: number;
    recent_window?: number;
    failover_enabled?: boolean;
    failover_slow_runs?: number;
    failover_cooldown_minutes?: number;
    failover_recovery_minutes?: number;
    failover_min_samples?: number;
    group_slow_alert_minutes?: number;
  } | null;
  /** The ONE collapsed gateway-level episode (every deployment failing / readiness unhealthy). */
  gateway_alert?: { alerting: boolean; fail_streak: number; since: string | null; last_alerted_at: string | null; last_reason: string | null };
  /** OCP-949: model groups with no healthy deployment (the only per-group alert). */
  group_alerting_count?: number;
  /** OCP-1028: model groups whose deployments all answer, only slowly, and whose critical alert has not gone out yet. */
  group_slow_pending_count?: number;
  failed_over_count?: number;
  held_count?: number;
  items: ProbeItem[];
}

const STATUS_LABEL: Record<ProbeStatus, string> = {
  ok: 'OK',
  slow: 'Slow',
  error: 'Error',
  timeout: 'Timeout',
};

function badgeClasses(status: ProbeStatus | null): string {
  if (status === 'ok') return 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-200 dark:ring-green-800';
  if (status === 'slow') return 'bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 ring-amber-200 dark:ring-amber-800';
  if (status === null) return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 ring-gray-200 dark:ring-gray-700';
  return 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-red-200 dark:ring-red-800';
}

function stripClasses(status: ProbeStatus): string {
  if (status === 'ok') return 'bg-green-500/80';
  if (status === 'slow') return 'bg-amber-500';
  return 'bg-red-500';
}

function relative(iso: string | null): string {
  if (!iso) return '-';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

export function GatewayDeploymentProbeCard() {
  const [data, setData] = useState<ProbeView | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await api<ProbeView>('/v1/admin/gateway/deployment-probes?recent=30'));
    } catch (err) {
      setLoadError(err instanceof ApiError ? `Could not load deployment probes (HTTP ${err.status}).` : 'Could not load deployment probes.');
    } finally {
      setLoading(false);
    }
  }, []);

  const runNow = useCallback(async () => {
    setRunning(true);
    setLoadError(null);
    try {
      const res = await api<{ view: ProbeView }>('/v1/admin/gateway/deployment-probes/run', { method: 'POST', body: {} });
      setData(res.view);
    } catch (err) {
      setLoadError(err instanceof ApiError ? `Probe run failed (HTTP ${err.status}).` : 'Probe run failed.');
    } finally {
      setRunning(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const t = data?.thresholds ?? null;

  return (
    <section
      data-testid="gateway-deployment-probe-card"
      className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Deployment probes</h2>
          <p className="mt-1 max-w-2xl text-xs text-gray-500 dark:text-gray-400">
            A small real request to every deployment
            {t ? ` every ${t.interval_minutes} minutes` : ''}: an embedding for embedding models, a 1-token
            answer for chat models. Catches a provider that stalls while the gateway itself stays healthy.
            {t
              ? ` A deployment is slow over ${t.embedding_latency_ms} ms (embedding) / ${t.chat_latency_ms} ms (chat), or when the p95 of its last ${t.p95_window ?? 30} probes is over ${t.embedding_p95_ms ?? '-'} ms / ${t.chat_p95_ms ?? '-'} ms while at least half of the last ${t.recent_window ?? 8} probes are slow too. It is unhealthy after ${t.fail_streak} failed or ${t.failover_slow_runs ?? 3} not-good probes in a row: orboto Control then moves its group to a healthy sibling (Provider failover below). Only a group with no healthy deployment alerts; if every deployment fails at once, one gateway-level alert replaces those.`
              : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void runNow()}
            disabled={running || loading || !data?.configured || !data?.enabled}
            className="rounded bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {running ? 'Probing…' : 'Probe now'}
          </button>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading || running}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {loadError && (
        <div role="alert" className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {loadError}
        </div>
      )}

      {!loadError && data && !data.configured && (
        <div className="mt-4 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          No gateway base URL or master key configured - no deployment is probed.
        </div>
      )}

      {!loadError && data && data.configured && !data.enabled && (
        <div className="mt-4 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          Deployment probes are switched off (<span className="font-mono">ai.gateway_probe_enabled</span>). A stalled provider will not alert and no model group fails over.
        </div>
      )}

      {!loadError && data && data.configured && data.gateway_alert?.alerting && (
        <div role="alert" data-testid="deployment-probe-gateway-alert" className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <span className="font-semibold">Gateway-level fault: every deployment is failing.</span>{' '}
          {data.gateway_alert.last_reason ? `${data.gateway_alert.last_reason}. ` : ''}
          Check the gateway health card first; group alerts and automatic failover are held back until a deployment answers.
        </div>
      )}

      {!loadError && data && data.configured && (data.group_alerting_count ?? 0) > 0 && (
        <div role="alert" data-testid="deployment-probe-alert" className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <span className="font-semibold">
            {data.group_alerting_count} model group{data.group_alerting_count === 1 ? '' : 's'} with no healthy deployment.
          </span>{' '}
          Tenant calls on {data.group_alerting_count === 1 ? 'it' : 'them'} fail or wait; the runbook is deploy/runbooks/ai-gateway-failover.md.
        </div>
      )}

      {!loadError && data && data.configured && (data.group_slow_pending_count ?? 0) > 0 && (
        <div data-testid="deployment-probe-slow-pending" className="mt-4 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          <span className="font-semibold">
            {data.group_slow_pending_count} model group{data.group_slow_pending_count === 1 ? '' : 's'} answering only slowly.
          </span>{' '}
          Every deployment of {data.group_slow_pending_count === 1 ? 'it' : 'each'} answers, none is good; the critical alert follows {data.thresholds?.group_slow_alert_minutes !== undefined ? `after ${data.thresholds.group_slow_alert_minutes} min` : 'after the configured limit'} unless a deployment is good again first. Status: Provider failover card.
        </div>
      )}

      {!loadError && data && data.configured && (data.failed_over_count ?? 0) > 0 && (
        <div data-testid="deployment-probe-failed-over" className="mt-4 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          <span className="font-semibold">
            {data.failed_over_count} model group{data.failed_over_count === 1 ? '' : 's'} failed over.
          </span>{' '}
          A healthy sibling carries the traffic; the preferred order returns after a stable recovery.
        </div>
      )}

      {!loadError && data && data.configured && data.items.length === 0 && (
        <p className="mt-4 text-sm text-gray-500">No probe has run yet.</p>
      )}

      {!loadError && data && data.items.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Model</th>
                <th className="px-3 py-2">Provider</th>
                <th className="px-3 py-2 text-right" title="Active order (preferred order in brackets when it differs)">Order</th>
                <th className="px-3 py-2">Last probe</th>
                <th className="px-3 py-2 text-right">Latency</th>
                <th className="px-3 py-2 text-right" title="95th percentile over the summary window">p95</th>
                <th className="px-3 py-2">Since</th>
                <th className="px-3 py-2">Recent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {data.items.map((i) => (
                <tr key={i.model_id} data-testid="deployment-probe-row">
                  <td className="px-3 py-2">
                    <div className="font-mono text-gray-900 dark:text-gray-100">{i.model_name}</div>
                    <div className="text-xs text-gray-500">{i.kind}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{i.provider ?? '-'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400" data-testid={`deployment-probe-order-${i.model_id}`}>
                    {i.order ?? '-'}
                    {i.preferred_order != null && i.preferred_order !== i.order && <span className="text-xs text-amber-800 dark:text-amber-300"> (pref. {i.preferred_order})</span>}
                    {i.active && <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">active</div>}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      data-testid={`deployment-probe-status-${i.model_id}`}
                      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset ${badgeClasses(i.status)}`}
                    >
                      {i.status ? STATUS_LABEL[i.status] : 'Not probed'}
                    </span>
                    <div className="mt-0.5 text-xs text-gray-500">{relative(i.probed_at)}</div>
                    {i.error && <div className="mt-0.5 max-w-sm break-words text-xs text-gray-700 dark:text-gray-300">{i.error}</div>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{i.latency_ms !== null ? `${i.latency_ms} ms` : '-'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                    {i.window.p95_latency_ms !== null ? `${i.window.p95_latency_ms} ms` : '-'}
                    {i.p95?.breach && (
                      <div data-testid={`deployment-probe-p95-${i.model_id}`} className="text-xs font-semibold text-amber-800 dark:text-amber-300" title={`p95 ${i.p95.ms} ms over the last probes, limit ${i.p95.threshold_ms} ms`}>
                        p95 {i.p95.ms} ms &gt; {i.p95.threshold_ms}
                      </div>
                    )}
                    {i.recent_ok && i.recent_ok.probes > 0 && (
                      <div data-testid={`deployment-probe-recent-ok-${i.model_id}`} className="text-xs text-gray-500" title="Ok probes among the recent window - a failover candidate needs 3 of 4">
                        {i.recent_ok.ok}/{i.recent_ok.probes} recent ok
                      </div>
                    )}
                    {i.recent_slow && i.recent_slow.samples > 0 && (
                      <div data-testid={`deployment-probe-recent-${i.model_id}`} className="text-xs text-gray-500" title="Recent answered probes over the p95 limit - the current finding behind a p95 breach">
                        {i.recent_slow.slow}/{i.recent_slow.samples} recent slow
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400" title={i.since ? fmtDateTime(i.since) : undefined}>
                    {i.since ? `${i.ok ? 'ok' : 'failing'} ${relative(i.since)}` : '-'}
                    {!i.ok && i.last_ok_at && <div>last ok {relative(i.last_ok_at)}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex w-40 gap-[2px]">
                      {[...i.recent].reverse().map((s) => (
                        <span
                          key={s.observed_at}
                          title={`${fmtDateTime(s.observed_at)} - ${STATUS_LABEL[s.status]}${s.latency_ms !== null ? ` ${s.latency_ms} ms` : ''}${s.error ? `: ${s.error}` : ''}`}
                          className={`h-4 flex-1 rounded-sm ${stripClasses(s.status)}`}
                        />
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
